import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Star, PenLine } from "lucide-react";
import { Stars } from "@/components/StarPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];
const PROPERTY_NAMES = {
  "de-la-vie": "De La Vie Maison",
  heritage: "Heritage",
  hearthstead: "Hearthstead Cottage",
};

const RATINGS = [
  ["Check-in", "5.0"],
  ["Cleanliness", "4.71"],
  ["Accuracy", "4.71"],
  ["Communication", "5.0"],
  ["Location", "4.71"],
  ["Value", "4.71"],
];

const REVIEWS = [
  { quote: "Great stay and huge place.", name: "Ariel" },
  { quote: "Host was wonderful, place was wonderful — would recommend.", name: "Amanda" },
  { quote: "House was perfect and it was in a good neighborhood and very peaceful.", name: "Richard" },
  { quote: "Great host. They went over and beyond with the welcoming — this was a very special stay.", name: "LaConya" },
  { quote: "Nice house to stay!", name: "Isiaias" },
];

export default function Reviews() {
  const [live, setLive] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((r) => setLive(r.data))
      .catch(() => {});
  }, []);

  const all = [
    ...live.map((r) => ({ id: r.id, quote: r.quote, name: r.name, rating: r.rating, property: PROPERTY_NAMES[r.property] })),
    ...REVIEWS.map((r) => ({ id: r.name, ...r })),
  ];

  return (
    <section data-testid="reviews-section" className="bg-[#f7f2e7]/90 text-[#16301f]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="text-center mb-14"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#16301f]/60 flex items-center justify-center gap-4">
            <span className="h-px w-12 bg-[#16301f]/40 inline-block" />
            Guest Words
            <span className="h-px w-12 bg-[#16301f]/40 inline-block" />
          </p>
          <h2 className="font-display font-medium text-4xl sm:text-5xl mt-6">Kind words</h2>
          <p className="font-script text-3xl sm:text-4xl text-[#1a3324] mt-3 -rotate-1 inline-block">
            Guests say it best.
          </p>
        </motion.div>

        <motion.div
          data-testid="guest-ratings"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-[#16301f]/10 border border-[#16301f]/10 mb-14"
        >
          {RATINGS.map(([label, score]) => (
            <div key={label} data-testid={`rating-${label.toLowerCase()}`} className="bg-[#fbf8f0] p-5 sm:p-6 text-center">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#16301f]/50">{label}</p>
              <p className="font-display text-2xl sm:text-3xl mt-2 flex items-center justify-center gap-1.5">
                <Star className="w-4 h-4 text-[#16301f] fill-[#16301f]" strokeWidth={1} />
                {score}
              </p>
              <div className="relative h-px bg-[#16301f]/10 mt-3">
                <div
                  className="absolute left-0 top-0 h-px bg-[#16301f]"
                  style={{ width: `${(parseFloat(score) / 5) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {all.map((r, i) => (
            <motion.figure
              key={r.id}
              data-testid={`review-card-${i + 1}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.12, ease: EASE }}
              className="border border-[#16301f]/10 bg-[#fbf8f0] p-8 flex flex-col"
            >
              <div className="flex items-center justify-between">
                <p className="font-script text-4xl text-[#16301f]/25 leading-none">“</p>
                {r.rating && <Stars rating={r.rating} />}
              </div>
              <blockquote className="font-display italic text-xl leading-relaxed mt-2 flex-1">
                {r.quote}
              </blockquote>
              <figcaption className="mt-6 pt-5 border-t border-[#16301f]/10 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full border border-[#16301f]/25 flex items-center justify-center font-display text-sm text-[#16301f]">
                  {r.name.charAt(0)}
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-[#16301f]/80">{r.name}</p>
                  {r.property && (
                    <p className="text-[10px] tracking-[0.15em] text-[#16301f]/50 mt-0.5">{r.property}</p>
                  )}
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="text-center mt-14"
        >
          <p className="text-sm text-[#16301f]/60 font-light">Stayed with us recently?</p>
          <a
            data-testid="leave-review-link"
            href="/review"
            className="inline-flex items-center gap-2 mt-4 border border-[#16301f] text-[#16301f] text-xs uppercase tracking-[0.25em] px-7 py-3.5 hover:bg-[#16301f] hover:text-[#f7f2e7] transition-colors duration-300"
          >
            <PenLine className="w-4 h-4" strokeWidth={1.5} />
            Leave a review
          </a>
        </motion.div>
      </div>
    </section>
  );
}
